// Reviews: delivered customers to ask for a review on WhatsApp.
//
// Built for a phone first. Each customer has one green button that opens their
// WhatsApp chat with the message already typed — the staff member only taps
// Send. "Send to next N" walks through N customers one tap at a time.
//
// The server remembers who was messaged (and by whom), so several people can
// work the same list without anyone getting the message twice.
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '../api';
import { useAuth } from '../auth';

const DEFAULT_MESSAGE =
`Hi {name}! 👋 This is {staff} from Think'th Goods.

Your order {order} ({item}) reached you a few days ago — hope you're loving it!

Could you take 30 seconds to tell us what you think? A photo of you wearing it would make our day 🙌`;

const MSG_KEY = 'reviews.message';
const loadMsg = () => { try { return localStorage.getItem(MSG_KEY) || DEFAULT_MESSAGE; } catch (e) { return DEFAULT_MESSAGE; } };
const saveMsg = (m) => { try { localStorage.setItem(MSG_KEY, m); } catch (e) { /* fine */ } };

const firstName = (s) => (s || '').trim().split(/\s+/)[0] || 'there';
const titleCase = (s) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
const itemText = (items) => items.map(i => i.name).filter(Boolean).join(', ');

const fill = (msg, o, staff) => msg
  .replace(/\{name\}/g, titleCase(firstName(o.name)))
  .replace(/\{order\}/g, o.orderNumber || '')
  .replace(/\{item\}/g, itemText(o.items))
  .replace(/\{staff\}/g, staff);

const waLink = (o, msg, staff) => `https://wa.me/${o.waNumber}?text=${encodeURIComponent(fill(msg, o, staff))}`;

const ago = (d) => {
  const days = Math.floor((Date.now() - new Date(d)) / 86400000);
  return days <= 0 ? 'today' : days === 1 ? 'yesterday' : `${days} days ago`;
};

const GREEN = '#1f8f4e';
const btn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
  padding: '10px 14px', borderRadius: 10, fontSize: 14, fontWeight: 600,
  textDecoration: 'none', cursor: 'pointer', border: 'none'
};

function Chip({ active, onClick, children }) {
  return (
    <button onClick={onClick} style={{
      ...btn, padding: '7px 14px', fontSize: 13,
      background: active ? '#6E1F2A' : '#fff', color: active ? '#F8F3EB' : '#4a3840',
      border: '1px solid ' + (active ? '#6E1F2A' : '#e6dccc')
    }}>{children}</button>
  );
}

export default function Reviews() {
  const { user } = useAuth();
  const staff = titleCase(firstName(user?.name || user?.email?.split('@')[0] || ''));

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [show, setShow] = useState('todo');
  const [search, setSearch] = useState('');
  const [message, setMessage] = useState(loadMsg);
  const [editing, setEditing] = useState(false);
  const [batchSize, setBatchSize] = useState(10);
  const [queue, setQueue] = useState(null);   // { ids: [...], pos }

  const load = async () => {
    try {
      const res = await axios.get(`${API}/api/reviews/delivered`, { params: { show } });
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setData(null); load(); }, [show]);

  const list = useMemo(() => {
    const rows = data?.orders || [];
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter(o => [o.name, o.phone, o.orderNumber, o.city, itemText(o.items)]
      .join(' ').toLowerCase().includes(s));
  }, [data, search]);

  const patchRow = (id, patch, shift) => setData(d => d && ({
    ...d,
    orders: d.orders.map(o => o.id === id ? { ...o, ...patch } : o),
    counts: { todo: d.counts.todo - shift, asked: d.counts.asked + shift }
  }));

  const markAsked = async (o, undo = false) => {
    patchRow(o.id, undo ? { askedAt: null, askedBy: null } : { askedAt: new Date().toISOString(), askedBy: user?.email || 'you' }, undo ? -1 : 1);
    try { await axios.post(`${API}/api/reviews/${o.id}/asked`, undo ? { undo: true } : {}); }
    catch (e) { setError('Could not save — ' + (e.response?.data?.message || e.message)); }
  };

  // ---- Send to next N -------------------------------------------------
  const startBatch = () => {
    const ids = list.filter(o => !o.askedAt && o.waNumber).slice(0, batchSize).map(o => o.id);
    if (ids.length) setQueue({ ids, pos: 0 });
  };
  const byId = (id) => (data?.orders || []).find(o => o.id === id);
  const current = queue ? byId(queue.ids[queue.pos]) : null;
  const advance = () => setQueue(q => (q.pos + 1 >= q.ids.length ? { ...q, pos: q.ids.length } : { ...q, pos: q.pos + 1 }));
  const batchDone = queue && queue.pos >= queue.ids.length;

  const waitingCount = list.filter(o => !o.askedAt && o.waNumber).length;

  return (
    <div style={{ maxWidth: 760, margin: '0 auto' }}>
      <div className="card">
        <div className="card-title">Ask for reviews</div>
        <div style={{ fontSize: 14, color: '#7d6a63', marginBottom: 14 }}>
          Orders from #7521 on, delivered at least 4 days ago — oldest first. Tap <b style={{ color: GREEN }}>WhatsApp</b>, the message is already typed — just press Send.
        </div>

        {/* The message */}
        <div style={{ background: '#f8f3eb', border: '1px solid #e6dccc', borderRadius: 10, padding: 12, marginBottom: 14 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: '#7d6a63', fontWeight: 600 }}>YOUR MESSAGE</span>
            <button onClick={() => setEditing(!editing)} style={{ ...btn, padding: '4px 10px', fontSize: 12, background: '#fff', border: '1px solid #e6dccc', color: '#6E1F2A' }}>
              {editing ? 'Done' : 'Edit'}
            </button>
          </div>
          {editing ? (
            <>
              <textarea value={message} rows={7}
                onChange={e => { setMessage(e.target.value); saveMsg(e.target.value); }}
                style={{ width: '100%', boxSizing: 'border-box', fontSize: 14, padding: 10, borderRadius: 8, border: '1px solid #e6dccc', fontFamily: 'inherit' }} />
              <div style={{ fontSize: 12, color: '#7d6a63', marginTop: 6, lineHeight: 1.5 }}>
                These fill in by themselves: <code>{'{name}'}</code> customer's first name · <code>{'{item}'}</code> what they bought · <code>{'{order}'}</code> order number · <code>{'{staff}'}</code> your name.
                {message !== DEFAULT_MESSAGE && (
                  <> <button onClick={() => { setMessage(DEFAULT_MESSAGE); saveMsg(DEFAULT_MESSAGE); }}
                    style={{ background: 'none', border: 'none', color: '#6E1F2A', textDecoration: 'underline', cursor: 'pointer', fontSize: 12, padding: 0 }}>Reset to default</button></>
                )}
              </div>
            </>
          ) : (
            <div style={{ whiteSpace: 'pre-wrap', fontSize: 14, color: '#2e1f23' }}>
              {list[0] ? fill(message, list[0], staff) : message}
            </div>
          )}
        </div>

        {/* Send to next N */}
        {show === 'todo' && !queue && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 14 }}>Send to next</span>
            <input type="number" min={1} max={waitingCount || 1} value={batchSize}
              onChange={e => setBatchSize(Math.max(1, parseInt(e.target.value, 10) || 1))}
              style={{ width: 64, fontSize: 15, padding: '8px', borderRadius: 8, border: '1px solid #e6dccc', textAlign: 'center' }} />
            <span style={{ fontSize: 14 }}>customers</span>
            <button onClick={startBatch} disabled={!waitingCount}
              style={{ ...btn, background: GREEN, color: '#fff', opacity: waitingCount ? 1 : 0.5 }}>Start</button>
          </div>
        )}

        {queue && (
          <div style={{ border: `2px solid ${GREEN}`, borderRadius: 12, padding: 14, background: '#eef8f1' }}>
            {batchDone ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>✅ Done — {queue.ids.length} customers messaged</div>
                <button onClick={() => { setQueue(null); load(); }} style={{ ...btn, background: '#6E1F2A', color: '#fff' }}>Close</button>
              </div>
            ) : current && (
              <>
                <div style={{ fontSize: 13, color: '#2e7d50', fontWeight: 600, marginBottom: 6 }}>
                  {queue.pos + 1} of {queue.ids.length}
                </div>
                <div style={{ fontSize: 17, fontWeight: 700 }}>{current.name}</div>
                <div style={{ fontSize: 13, color: '#7d6a63', marginBottom: 12 }}>
                  {current.orderNumber} · {itemText(current.items)}
                </div>
                <a href={waLink(current, message, staff)} target="_blank" rel="noopener noreferrer"
                  onClick={() => { markAsked(current); advance(); }}
                  style={{ ...btn, background: GREEN, color: '#fff', width: '100%', boxSizing: 'border-box', padding: '14px', fontSize: 16 }}>
                  Open WhatsApp →
                </a>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 10 }}>
                  <button onClick={advance} style={{ ...btn, padding: '6px 10px', fontSize: 13, background: 'none', color: '#7d6a63' }}>Skip this one</button>
                  <button onClick={() => { setQueue(null); load(); }} style={{ ...btn, padding: '6px 10px', fontSize: 13, background: 'none', color: '#8c3242' }}>Stop</button>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
        <Chip active={show === 'todo'} onClick={() => { setShow('todo'); setQueue(null); }}>To ask{data ? ` (${data.counts.todo})` : ''}</Chip>
        <Chip active={show === 'asked'} onClick={() => { setShow('asked'); setQueue(null); }}>Messaged{data ? ` (${data.counts.asked})` : ''}</Chip>
        <input placeholder="Search name, phone, order…" value={search} onChange={e => setSearch(e.target.value)}
          style={{ flex: '1 1 180px', fontSize: 14, padding: '8px 10px', borderRadius: 10, border: '1px solid #e6dccc' }} />
      </div>

      {error && <div style={{ color: '#b3261e', marginBottom: 12 }}>{error}</div>}
      {!data && !error && <div style={{ color: '#7d6a63' }}>Loading…</div>}
      {data && !list.length && (
        <div className="card" style={{ textAlign: 'center', color: '#7d6a63' }}>
          {show === 'todo' ? 'Nobody left to ask 🎉' : 'Nobody messaged yet.'}
        </div>
      )}

      {list.map(o => (
        <div key={o.id} className="card" style={{ padding: 14, marginBottom: 10, opacity: o.askedAt && show === 'todo' ? 0.55 : 1 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700 }}>{o.name || 'No name'}</div>
              <div style={{ fontSize: 13, color: '#7d6a63' }}>
                {o.orderNumber}{o.city ? ` · ${o.city}` : ''} · delivered {ago(o.deliveredAt)}
              </div>
            </div>
            {o.phone && (
              <a href={`tel:${o.phone}`} style={{ fontSize: 13, color: '#4a3840', whiteSpace: 'nowrap', textDecoration: 'none' }}>📞 {o.phone}</a>
            )}
          </div>

          <div style={{ fontSize: 14, margin: '8px 0' }}>
            {o.items.map((i, k) => (
              <div key={k}>• {i.name} <span style={{ color: '#7d6a63' }}>— {i.color}, {i.size}{i.qty > 1 ? ` ×${i.qty}` : ''}</span></div>
            ))}
          </div>

          {o.hadReturn && (
            <div style={{ fontSize: 12, color: '#8a5a2b', background: '#fbeee5', borderRadius: 6, padding: '4px 8px', marginBottom: 8, display: 'inline-block' }}>
              ⚠️ This order had a return/exchange — check before asking
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {o.waNumber ? (
              <a href={waLink(o, message, staff)} target="_blank" rel="noopener noreferrer"
                onClick={() => !o.askedAt && markAsked(o)}
                style={{ ...btn, background: GREEN, color: '#fff', flex: '1 1 auto' }}>WhatsApp</a>
            ) : (
              <span style={{ fontSize: 13, color: '#b3261e' }}>No phone number</span>
            )}
            {o.askedAt ? (
              <>
                <span style={{ fontSize: 12, color: '#2e7d50' }}>
                  ✓ {o.askedBy ? o.askedBy.split('@')[0] : 'Messaged'} · {ago(o.askedAt)}
                </span>
                <button onClick={() => markAsked(o, true)}
                  style={{ ...btn, padding: '6px 10px', fontSize: 12, background: 'none', color: '#7d6a63', textDecoration: 'underline' }}>Undo</button>
              </>
            ) : (
              <button onClick={() => markAsked(o)}
                style={{ ...btn, background: '#fff', color: '#4a3840', border: '1px solid #e6dccc', fontSize: 13 }}>Mark done</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
